import React, { useState } from 'react';
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_LENGTH_MESSAGE } from '@/constants/users';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { User } from '@/types';
import { adminAPI } from '@/services/api';
import AlertDialog from '@/components/users/management/common/dialogs/AlertDialog';
import { handleAPIError } from '@/utils';

interface EditUserDialogProps {
  user: User | null;
  loading: boolean;
  onClose: () => void;
  onSave: (user: User, updates?: { auth_type?: string; new_password?: string }) => Promise<void>;
  onPasswordChange?: (user: User) => void;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  user,
  loading,
  onClose,
  onSave,
  onPasswordChange: _onPasswordChange,
}) => {
  const [editingUser, setEditingUser] = useState<User | null>(user);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('');

  React.useEffect(() => {
    setEditingUser(user);
    setNewPassword('');
    setConfirmPassword('');
  }, [user]);

  if (!editingUser) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const updates: { auth_type?: string; new_password?: string } = {};
    
    // 로그인 유형이 변경되었는지 확인
    if (editingUser.auth_type !== user?.auth_type) {
      updates.auth_type = editingUser.auth_type;
    }
    
    // 비밀번호가 입력되었는지 확인
    if (newPassword && confirmPassword) {
      if (newPassword !== confirmPassword) {
        setAlertTitle('오류');
        setAlertMessage('비밀번호가 일치하지 않습니다.');
        setAlertOpen(true);
        return;
      }
      if (newPassword.length < PASSWORD_MIN_LENGTH || newPassword.length > PASSWORD_MAX_LENGTH) {
        setAlertTitle('오류');
        setAlertMessage(PASSWORD_LENGTH_MESSAGE);
        setAlertOpen(true);
        return;
      }
      updates.new_password = newPassword;
    }
    
    // customer_name이 변경되었는지 확인하고 customer 업데이트
    if (editingUser.customer && user?.customer && 
        editingUser.customer.customer_name !== user.customer.customer_name) {
      try {
        await adminAPI.updateCustomer(editingUser.customer.id, {
          customer_name: editingUser.customer.customer_name
        });
      } catch (error: any) {
        setAlertTitle('오류');
        setAlertMessage(handleAPIError(error, '고객사명 수정에 실패했습니다.'));
        setAlertOpen(true);
        return;
      }
    }
    
    await onSave(editingUser, Object.keys(updates).length > 0 ? updates : undefined);
  };

  return (
    <Dialog open={!!user}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" onClose={onClose}>
        <DialogHeader>
          <DialogTitle>사용자 정보 수정</DialogTitle>
          <DialogDescription>{editingUser.email}</DialogDescription>
        </DialogHeader>
        <div className="mt-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 이름과 조직을 좌우로 배치 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit_user_name">이름</Label>
              <Input
                id="edit_user_name"
                value={editingUser.name || ''}
                onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                placeholder="이름을 입력하세요"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_user_customer">조직</Label>
              <Input
                id="edit_user_customer"
                value={editingUser.customer?.customer_name || ''}
                onChange={(e) => {
                  // 조직명 변경: customer 객체의 customer_name 수정
                  if (editingUser.customer) {
                    setEditingUser({
                      ...editingUser,
                      customer: {
                        ...editingUser.customer,
                        customer_name: e.target.value
                      }
                    });
                  }
                }}
                placeholder="회사명을 입력하세요"
              />
            </div>
          </div>
          {/* 이메일 한 줄 (수정 불가) */}
          <div className="space-y-2">
            <Label htmlFor="edit_user_email">이메일</Label>
            <Input
              id="edit_user_email"
              value={editingUser.email}
              readOnly
              className="bg-gray-100"
            />
          </div>
          {/* 역할과 사용자 권한을 좌우로 배치 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit_user_role">역할</Label>
              <Select
                value={editingUser.role}
                onValueChange={(value) => {
                  const newRole = value as 'user' | 'admin' | 'saas_admin';
                  setEditingUser({
                    ...editingUser,
                    role: newRole,
                    permission: newRole === 'user' ? editingUser.permission : undefined
                  });
                }}
                disabled={editingUser.role === 'saas_admin'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">일반 사용자</SelectItem>
                  <SelectItem value="admin">기업 관리자</SelectItem>
                  {editingUser.role === 'saas_admin' && (
                    <SelectItem value="saas_admin">SaaS 관리자 (CLI에서만 설정 가능)</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {editingUser.role === 'saas_admin' && (
                <p className="text-xs text-red-600 mt-1">
                  ⚠️ SaaS 관리자 역할은 GUI에서 변경할 수 없습니다. CLI에서만 설정 가능합니다.
                </p>
              )}
            </div>
            {editingUser.role === 'user' && (
              <div className="space-y-2">
                <Label htmlFor="edit_user_permission">사용자 권한 (세부 권한)</Label>
                <Select
                  value={editingUser.permission || 'readwrite'}
                  onValueChange={(value) => setEditingUser({ ...editingUser, permission: value as 'readwrite' | 'readonly' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="readwrite">읽기/쓰기</SelectItem>
                    <SelectItem value="readonly">읽기 전용</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {editingUser.role !== 'user' && (
              <div className="space-y-2">
                <Label htmlFor="edit_user_permission_placeholder">사용자 권한</Label>
                <Input
                  id="edit_user_permission_placeholder"
                  value="해당 없음"
                  readOnly
                  className="bg-gray-100"
                />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit_user_auth_type">로그인 유형</Label>
            <Select
              value={editingUser.auth_type === 'pending_oauth' ? 'oauth' : (editingUser.auth_type || 'password')}
              onValueChange={(value) => {
                const selectedType = value as 'password' | 'oauth';
                // ID/PW => OAuth로 변경 시 pending_oauth로 설정 (자동)
                const finalAuthType = (editingUser.auth_type === 'password' && selectedType === 'oauth') 
                  ? 'pending_oauth' 
                  : selectedType;
                setEditingUser({ ...editingUser, auth_type: finalAuthType });
                // OAuth로 변경하면 비밀번호 필드 초기화
                if (finalAuthType === 'oauth' || finalAuthType === 'pending_oauth') {
                  setNewPassword('');
                  setConfirmPassword('');
                }
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="password">🔑 ID/PW 로그인</SelectItem>
                <SelectItem value="oauth">🔗 Google 로그인 (OAuth2)</SelectItem>
              </SelectContent>
            </Select>
            {editingUser.auth_type === 'pending_oauth' && (
              <p className="text-sm text-yellow-600 mt-1">
                ⏳ OAuth 전환 대기 중: 다음 로그인 시 Google 로그인 성공하면 OAuth로 전환됩니다.
              </p>
            )}
          </div>
          {(editingUser.auth_type === 'password' || editingUser.auth_type === 'pending_oauth') && (
            <div className="space-y-2">
              <Label htmlFor="edit_user_password">비밀번호 변경 (선택사항)</Label>
              <Input
                id="edit_user_password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="새 비밀번호 (변경하지 않으려면 비워두세요)"
                className="w-full"
              />
              <Input
                id="edit_user_password_confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="비밀번호 확인"
                className="w-full"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="edit_user_status">상태</Label>
            <Select
              value={editingUser.status}
              onValueChange={(value) => setEditingUser({ ...editingUser, status: value as 'active' | 'pending' | 'invited' | 'deleted' })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">대기 중</SelectItem>
                <SelectItem value="active">활성</SelectItem>
                <SelectItem value="invited">초대됨</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              비활성화된 사용자(pending, invited)도 수정할 수 있습니다.
            </p>
          </div>
          <div className="flex space-x-2 pt-4">
            <Button type="submit" disabled={loading}>
              {loading ? '저장 중...' : '저장'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              취소
            </Button>
          </div>
        </form>
        </div>
      </DialogContent>

      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        confirmText="확인"
        variant="destructive"
        onConfirm={() => setAlertOpen(false)}
      />
    </Dialog>
  );
};

export default EditUserDialog;

